import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { onAuthStateChanged, onIdTokenChanged, signInWithEmailAndPassword, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { ensureImpersonationWithinTtl } from '../services/impersonationService';
import { registerFCMTokenForCareUser } from '../services/fcmService';
import { onForegroundMessage } from '../firebase';
import { getRolePermissions } from '../constants/rolePermissions';
import { resetAuditActorCache } from '../services/auditService';
import {
  signInWithMicrosoft as msSignIn,
  linkMicrosoftAndFinalize,
  readSessionClaims,
  secondFactorSatisfied,
  hasMicrosoftProvider,
  hasPasswordProvider,
  enrolledFactors,
} from '../services/careAuthService';
import { useSessionGuard } from '../hooks/useSessionGuard';

const AuthContext = createContext(null);

const EMPTY_CLAIMS = { authTime: null, secondFactor: null, idpMfa: false, signInCountry: null, signInProvider: null };

function tsToMillis(v) {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState(EMPTY_CLAIMS);
  /** Bumped after MFA enrollment / Microsoft link so provider-derived state recomputes. */
  const [authVersion, setAuthVersion] = useState(0);

  const refreshProfile = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) {
      setProfile(null);
      return;
    }
    try {
      const profileSnap = await getDoc(doc(db, 'users', u.uid));
      setProfile(profileSnap.exists() ? { id: profileSnap.id, ...profileSnap.data() } : null);
    } catch (err) {
      console.warn('Failed to refresh profile:', err);
    }
  }, []);

  /** Re-reads ID-token claims (second factor, auth_time) — call after enrollment/link. */
  const refreshSession = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) {
      setClaims(EMPTY_CLAIMS);
      return;
    }
    try {
      await u.reload();
      setClaims(await readSessionClaims(u, true));
    } catch (err) {
      console.warn('Failed to refresh session claims:', err);
    }
    setAuthVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      resetAuditActorCache();
      if (!firebaseUser) {
        setProfile(null);
        setClaims(EMPTY_CLAIMS);
        setLoading(false);
        return;
      }
      try {
        const [profileSnap, sessionClaims] = await Promise.all([
          getDoc(doc(db, 'users', firebaseUser.uid)),
          readSessionClaims(firebaseUser).catch(() => EMPTY_CLAIMS),
        ]);
        setProfile(profileSnap.exists() ? { id: profileSnap.id, ...profileSnap.data() } : null);
        setClaims(sessionClaims);
      } catch (err) {
        console.warn('Failed to load user profile:', err);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Keep claims current across hourly token refreshes.
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (u) => {
      if (!u) return;
      try {
        setClaims(await readSessionClaims(u));
      } catch {
        /* ignore */
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (loading || !user || !profile) return;
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => {
        registerFCMTokenForCareUser(user.uid).catch((e) =>
          console.warn('FCM token registration failed:', e.message)
        );
      });
      return () => window.cancelIdleCallback(id);
    } else {
      const t = setTimeout(() => {
        registerFCMTokenForCareUser(user.uid).catch((e) =>
          console.warn('FCM token registration failed:', e.message)
        );
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [loading, user, profile]);

  useEffect(() => {
    if (loading || !user || !profile) return;
    if (String(profile.role || '').toLowerCase().trim() !== 'superuser') return;
    let cancelled = false;
    (async () => {
      try {
        const { changed } = await ensureImpersonationWithinTtl(profile);
        if (!cancelled && changed) await refreshProfile();
      } catch (err) {
        console.warn('Impersonation TTL check failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, user, profile, refreshProfile]);

  useEffect(() => {
    if (!user) return;
    const unsub = onForegroundMessage((payload) => {
      const title = payload?.notification?.title || 'Trilogy One';
      const body = payload?.notification?.body || '';
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, { body, icon: '/logo192.png' });
      }
    });
    return unsub;
  }, [user]);

  /**
   * Email/password sign-in. Throws `auth/multi-factor-auth-required` when the
   * account has a second factor; LoginPage resolves it with the MFA step.
   */
  const signIn = async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signInWithMicrosoft = async () => {
    await msSignIn();
  };

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  const linkMicrosoft = useCallback(async () => {
    const result = await linkMicrosoftAndFinalize();
    await Promise.all([refreshProfile(), refreshSession()]);
    return result;
  }, [refreshProfile, refreshSession]);

  // ---- Security gates -------------------------------------------------------
  const authMethod = profile?.authMethod || 'email_password';
  const mfaSatisfied = secondFactorSatisfied(claims);
  const mfaExemptUntilMs = tsToMillis(profile?.mfaExemptUntil);
  const mfaGraceActive = Boolean(mfaExemptUntilMs && mfaExemptUntilMs > Date.now());
  // `user` is the same object after reload()/enroll(), so authVersion forces a recompute.
  const providerState = useMemo(
    () => ({
      version: authVersion,
      microsoftLinked: hasMicrosoftProvider(user),
      hasPassword: hasPasswordProvider(user),
      factors: enrolledFactors(user),
    }),
    [user, authVersion],
  );
  const mfaEnrolled = providerState.factors.length > 0;

  /**
   * Which full-screen gate (if any) must be shown before the app:
   *  - 'connect-microsoft': Trilogy staff bootstrapped with a temp password, not yet linked
   *  - 'change-password'  : temp password in use
   *  - 'enroll-mfa'       : no second factor on this session and no grace remaining
   */
  let securityGate = null;
  if (user && profile) {
    if (authMethod === 'microsoft_pending' && !providerState.microsoftLinked) {
      securityGate = 'connect-microsoft';
    } else if (profile.mustChangePassword === true) {
      securityGate = 'change-password';
    } else if (!mfaSatisfied && !mfaGraceActive) {
      securityGate = 'enroll-mfa';
    }
  }

  const { idleWarning, idleRemainingMs, staySignedIn } = useSessionGuard({
    active: Boolean(user && profile),
    authTimeS: claims.authTime,
    signOut,
  });

  const roleLower = String(profile?.role || '').toLowerCase().trim();
  const isSuperuser = roleLower === 'superuser';
  const permissions = getRolePermissions(roleLower);
  const impersonatingTenantId =
    typeof profile?.impersonatingTenantId === 'string' && profile.impersonatingTenantId.trim()
      ? profile.impersonatingTenantId.trim()
      : null;

  const value = {
    user,
    profile,
    loading,
    signIn,
    signOut,
    refreshProfile,
    /** Role capability map — see src/constants/rolePermissions.js. */
    permissions,
    /** Org admin or superuser — tenant Admin Tools (billing, education, RPM admin, etc.). */
    isAdmin: permissions.canSeeAdminTools,
    isSupervisor: roleLower === 'supervisor',
    isSuperuser,
    /** Read-only, client/provider-scoped role. */
    isViewer: roleLower === 'viewer',
    impersonatingTenantId,
    canManageUsers: permissions.canManageUsers,
    canManagePathways: permissions.canManagePathways,
    canCreatePatients: permissions.canCreatePatients,
    canOverrideDuplicatePatients: permissions.canOverrideDuplicatePatients,
    canManagePatientDevices: permissions.canManagePatientDevices,
    canManagePatientPrograms: permissions.canManagePatientPrograms,
    canEditCareManagerAssignments: permissions.canEditCareManagerAssignments,
    canEditPatientPhoto: permissions.canEditPatientPhoto,
    canReassignReadings: permissions.canReassignReadings,
    canVoidReadings: permissions.canVoidReadings,
    canManagePatientLifecycle: permissions.canManagePatientLifecycle,
    canUseHelpdeskTools: permissions.canUseHelpdeskTools,
    /**
     * Billing finalizer: explicit per-user designation (billingPermissions on
     * the users doc), never implied by role. Superusers qualify while
     * impersonating so they can bootstrap/rescue a tenant.
     */
    canFinalizeBillingPeriods:
      isSuperuser || profile?.billingPermissions?.canFinalizePeriods === true,
    /** Set by backend when the account has a temporary password; forces the change-password screen. */
    mustChangePassword: profile?.mustChangePassword === true,

    // ---- Security hardening (docs/PLAN_SECURITY_HARDENING_2026_10.md) ----
    signInWithMicrosoft,
    linkMicrosoft,
    refreshSession,
    /** 'email_password' | 'microsoft_pending' | 'microsoft' */
    authMethod,
    isMicrosoftUser: authMethod === 'microsoft' || providerState.microsoftLinked,
    /** True when the account can change a password (has the password provider). */
    hasPasswordProvider: providerState.hasPassword,
    /** ID-token claims: authTime, secondFactor ('phone'|'totp'), idpMfa, signInCountry. */
    sessionClaims: claims,
    /** Second factor proven on this session (Firebase MFA or Entra/Duo via idpMfa). */
    mfaSatisfied,
    /** Firebase MFA factors enrolled on the account (not applicable to Microsoft users). */
    mfaFactors: providerState.factors,
    mfaEnrolled,
    mfaGraceActive,
    mfaExemptUntilMs,
    /** null | 'connect-microsoft' | 'change-password' | 'enroll-mfa' */
    securityGate,
    /** Show the "set up two-step verification" banner: in grace, not yet proven this session. */
    showMfaGraceBanner: Boolean(user && profile && !mfaSatisfied && mfaGraceActive && securityGate === null),
    idleWarning,
    idleRemainingMs,
    staySignedIn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
